import { z } from "zod";
import { apiListSchema } from "./list";
import {
  ClientDtoSchema,
  clientFromDto,
  ClientSchema,
  clientToDto,
} from "./client";

export enum PaymentAdjustmentType {
  Tour = "Tour",
  Season = "Season",
  Individual_Room = "Individual_Room",
  Solo_Supplement = "Solo_Supplement",
  Transfers = "Transfers",
  Luggage = "Luggage",
  Custom = "Custom",
}

export const paymentAdjustmentTypeEnumToNumber: Record<
  PaymentAdjustmentType,
  number
> = {
  Tour: 0,
  Season: 1,
  Solo_Supplement: 2,
  Individual_Room: 3,
  Transfers: 4,
  Luggage: 5,
  Custom: 6,
};

export const PaymentAdjustmentReservationClientDtoSchema = z.object({
  ClientId: z.number(),
  ReservationId: z.number(),
  PaymentAdjustmentId: z.number(),
  Client: ClientDtoSchema.nullish(),
});
export type PaymentAdjustmentReservationClientDto = z.infer<
  typeof PaymentAdjustmentReservationClientDtoSchema
>;
export const PaymentAdjustmentReservationClientSchema = z.object({
  clientId: z.string(),
  reservationId: z.string(),
  paymentAdjustmentId: z.string(),
  client: ClientSchema.nullish(),
});
export type PaymentAdjustmentReservationClient = z.infer<
  typeof PaymentAdjustmentReservationClientSchema
>;
export function paymentReservationClientAdjustmentToDto(
  dto: PaymentAdjustmentReservationClient,
): PaymentAdjustmentReservationClientDto {
  return {
    ClientId: parseInt(dto.clientId),
    ReservationId: parseInt(dto.reservationId),
    PaymentAdjustmentId: parseInt(dto.paymentAdjustmentId),
    Client: dto.client ? clientToDto(dto.client) : null,
  };
}

export function paymentReservationClientAdjustmentFromDto(
  dto: PaymentAdjustmentReservationClientDto,
): PaymentAdjustmentReservationClient {
  return {
    clientId: dto.ClientId.toString(),
    reservationId: dto.ReservationId.toString(),
    paymentAdjustmentId: dto.PaymentAdjustmentId.toString(),
    client: dto.Client ? clientFromDto(dto.Client) : null,
  };
}

export const paymentAdjustmentTypeNumToEnum = Object.entries(
  paymentAdjustmentTypeEnumToNumber,
).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {}) as Record<
  number,
  PaymentAdjustmentType
>;
export const PaymentAdjustmentTypesAsArray = Object.values(
  PaymentAdjustmentType,
);

export const CreatePaymentAdjustmentDtoSchema = z.object({
  Type: z.number().nullish(),
  Total: z.number(),
  ReservationId: z.number(),
  Pvp: z.number(),
  Markup: z.number(),
  Comissions: z.number().nullish(),
  Discount: z.number().nullish(),
  PaymentAdjustmentReservationClients: z
    .array(PaymentAdjustmentReservationClientDtoSchema)
    .nullish(),
});

export type CreatePaymentAdjustmentDto = z.infer<
  typeof CreatePaymentAdjustmentDtoSchema
>;

export const PaymentAdjustmentDtoSchema =
  CreatePaymentAdjustmentDtoSchema.extend({
    Id: z.number(),
  });

export type PaymentAdjustmentDto = z.infer<typeof PaymentAdjustmentDtoSchema>;

export const paymentAdjustmentDtoListSchema = apiListSchema(
  PaymentAdjustmentDtoSchema,
);

export const CreatePaymentAdjustmentSchema = z.object({
  type: z.nativeEnum(PaymentAdjustmentType).nullish(),
  total: z.number(),
  reservationId: z.number(),
  pvp: z.number(),
  markup: z.number(),
  comissions: z.number().nullish(),
  discount: z.number().nullish(),
  paymentAdjustmentReservationClients: z
    .array(PaymentAdjustmentReservationClientSchema)
    .nullish(),
});
export type CreatePaymentAdjustment = z.infer<
  typeof CreatePaymentAdjustmentSchema
>;
export const PaymentAdjustmentSchema = CreatePaymentAdjustmentSchema.extend({
  id: z.string().min(1),
});
export type PaymentAdjustment = z.infer<typeof PaymentAdjustmentSchema>;

export function createPaymentAdjustmentToDto(
  paymentAdjustment: CreatePaymentAdjustment,
): CreatePaymentAdjustmentDto {
  return {
    Comissions: paymentAdjustment.comissions,
    Markup: paymentAdjustment.markup,
    Pvp: paymentAdjustment.pvp,
    Total: paymentAdjustment.total,
    Type: paymentAdjustment.type
      ? paymentAdjustmentTypeEnumToNumber[paymentAdjustment.type]
      : null,
    ReservationId: paymentAdjustment.reservationId,
    Discount: paymentAdjustment.discount,
    PaymentAdjustmentReservationClients:
      paymentAdjustment.paymentAdjustmentReservationClients?.map((x) =>
        paymentReservationClientAdjustmentToDto(x),
      ),
  };
}

export function paymentAdjustmentFromDto(
  dto: PaymentAdjustmentDto,
): PaymentAdjustment {
  return {
    comissions: dto.Comissions,
    id: dto.Id.toString(),
    markup: dto.Markup,
    pvp: dto.Pvp,
    reservationId: dto.ReservationId,
    total: dto.Total,
    type: dto.Type ? paymentAdjustmentTypeNumToEnum[dto.Type] : null,
    discount: dto.Discount,
    paymentAdjustmentReservationClients:
      dto.PaymentAdjustmentReservationClients?.map((x) =>
        paymentReservationClientAdjustmentFromDto(x),
      ),
  };
}

export function paymentAdjustmentToDto(
  dto: PaymentAdjustment,
): PaymentAdjustmentDto {
  return {
    Id: Number.parseInt(dto.id),
    ...createPaymentAdjustmentToDto(dto),
  };
}
